
import { motion } from 'motion/react';
import { Link } from 'react-router';
import { ArrowRight, Compass, Shield, Star, Quote, CheckCircle2, Award, Users, Calendar, Heart, Send } from 'lucide-react';
import { TripCalculator } from './TripCalculator';
import { Gallery } from './Gallery';
import { SEO } from './SEO';
import { StructuredData, businessSchema, websiteSchema } from './StructuredData';

const reviews = [
  {
    name: "Lord Alistair Kensington",
    role: "British Diplomat",
    comment: "Skiva provided impeccable security and absolute luxury during our Johannesburg delegation. Their VIP airport transfer is world-class.",
    rating: 5,
    location: "London, UK"
  },
  {
    name: "Elena & Marcus Vance",
    role: "Private Safari Guests",
    comment: "Our luxury safari tour was nothing short of magical. The elite trackers got us up close with the Big Five while maintaining pure 5-star comfort.",
    rating: 5,
    location: "Geneva, Switzerland"
  },
  {
    name: "Sipho Dlamini",
    role: "Corporate Executive",
    comment: "We rely exclusively on Skiva for our executive board drop-offs and premium shuttle services. Punctual, discreet, and highly professional.",
    rating: 5,
    location: "Sandton, South Africa"
  },
  {
    name: "Sophia Martinez",
    role: "Travel Journalist",
    comment: "The guided city tour offered a breathtaking, profound look into Africa's rich heritage. Skiva Africa Tours truly redefines luxury travel.",
    rating: 5,
    location: "Madrid, Spain"
  }
];

export function Home() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Transport Services Johannesburg | Skiva Africa Tours"
        description="Skiva Africa Tours provides premium transport services in Johannesburg and across South Africa, including airport transfers, private transfers, shuttle services, corporate transport, group transportation and tours."
        canonical="https://skivatours.com/"
      />
      <StructuredData data={[businessSchema, websiteSchema]} />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero.jpg" 
            alt="Skiva Africa Tours vehicle on Nelson Mandela Bridge, Johannesburg" 
            className="w-full h-full object-cover"
            style={{ transform: 'scale(1.15)', transformOrigin: 'center 40%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/90"></div>
        </div>

        <div className="container mx-auto px-4 z-10 text-center mt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="text-gold-base text-xs sm:text-sm font-bold tracking-[0.35em] uppercase mb-4">
              Africa <span className="text-white">on Sight</span> &bull; Luxury in Motion
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-wider mb-6 leading-tight max-w-5xl mx-auto">
              Transport Services in <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Johannesburg</span> &amp; South Africa
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base md:text-lg text-white/80 max-w-3xl mx-auto mb-10 font-light tracking-wide leading-relaxed"
          >
            Premium airport transfers, private transport, corporate travel, group transportation and curated tours across Johannesburg and South Africa.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link 
              to="/services" 
              className="px-8 py-4 bg-gold-base text-black font-bold uppercase tracking-widest text-sm hover:bg-gold-light transition-colors rounded-sm flex items-center gap-2 group w-full sm:w-auto justify-center"
            >
              Explore Services
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a 
              href="#trip-calculator" 
              className="px-8 py-4 bg-transparent border border-white/30 text-white font-bold uppercase tracking-widest text-sm hover:bg-white/10 transition-colors rounded-sm w-full sm:w-auto justify-center flex"
            >
              Get a Quote
            </a>
          </motion.div>
        </div>

        {/* scroll indicator */}
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-gold-base rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-primary relative z-10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base">
                <Compass className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest text-white">Bespoke Itineraries</h3>
              <p className="text-white/50 font-light leading-relaxed">
                Tailor-made journeys designed specifically around your unique preferences and schedule. Learn more about our <Link to="/services" className="text-gold-base hover:underline font-medium">transport services</Link>.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col items-center text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base">
                <Star className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest text-white">5-Star Luxury</h3>
              <p className="text-white/50 font-light leading-relaxed">
                From premium vehicles to exclusive lodges, experience the very best Africa has to offer when you <Link to="/book" className="text-gold-base hover:underline font-medium">book a private transfer</Link> with us.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest text-white">Safe & Secure</h3>
              <p className="text-white/50 font-light leading-relaxed">
                Expert guides and robust security protocols ensure your absolute peace of mind.
              </p>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="inline-flex items-center text-gold-base font-bold text-xs uppercase tracking-widest hover:text-white transition-colors"
            >
              Explore Our Transport Services <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trip Calculator Section */}
      <TripCalculator />

      {/* Gallery Section */}
      <Gallery />

      {/* Premium Stats / Milestones Section */}
      <section className="py-20 bg-[#0a0a0a] border-t border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold-base/5 via-black to-black pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center max-w-6xl mx-auto">
            {[
              { icon: <Award className="w-6 h-6 text-gold-base" />, count: "15+", label: "Years of Excellence" },
              { icon: <Users className="w-6 h-6 text-gold-base" />, count: "12k+", label: "VIP Guests Served" },
              { icon: <Shield className="w-6 h-6 text-gold-base" />, count: "100%", label: "Safety & Compliance" },
              { icon: <Calendar className="w-6 h-6 text-gold-base" />, count: "24/7", label: "Concierge Dispatch" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 rounded-sm bg-white/[0.02] border border-white/5 hover:border-gold-base/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-gold-base/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-black text-white mb-1 tracking-tight">{stat.count}</div>
                <div className="text-xs text-white/50 uppercase tracking-widest font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Guest Reviews Section */}
      <section className="py-28 bg-[#141414] relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-base/10 border border-gold-base/20 text-gold-base mb-4 text-xs font-bold uppercase tracking-widest"
            >
              <Star className="w-3.5 h-3.5 fill-gold-base" /> Guest Testimonials
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-4"
            >
              Praised by <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Global Elite</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-white/60 max-w-2xl mx-auto font-light"
            >
              Read firsthand accounts from our esteemed clients who have experienced the absolute pinnacle of luxury, discretion, and security across Africa.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reviews.map((rev, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="p-8 rounded-sm bg-[#1c1c1c] border border-white/10 hover:border-gold-base/40 transition-all flex flex-col justify-between relative group shadow-xl"
              >
                <div className="absolute top-8 right-8 text-white/10 group-hover:text-gold-base/20 transition-colors">
                  <Quote className="w-12 h-12" />
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-6">
                    {Array.from({ length: rev.rating }).map((_, sIdx) => (
                      <Star key={sIdx} className="w-4 h-4 fill-gold-base text-gold-base" />
                    ))}
                  </div>
                  <p className="text-white/80 font-light leading-relaxed mb-8 text-base md:text-lg italic relative z-10">
                    "{rev.comment}"
                  </p>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-6 mt-auto">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold-base flex items-center justify-center text-black font-bold text-sm tracking-wider uppercase">
                      {rev.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm tracking-wide">{rev.name}</h4>
                      <p className="text-xs text-gold-base font-medium">{rev.role} · <span className="text-white/40">{rev.location}</span></p>
                    </div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-gold-base" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Elite Newsletter / Community Section */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,_var(--tw-gradient-stops))] from-gold-base/10 via-black to-black pointer-events-none" />
        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-10 md:p-16 rounded-sm bg-[#141414] border border-gold-base/20 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-base/5 rounded-full blur-3xl pointer-events-none" />
            <div className="w-14 h-14 rounded-full bg-gold-base/10 flex items-center justify-center mx-auto mb-6 text-gold-base">
              <Heart className="w-7 h-7" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-4">
              Join the <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Skiva Elite Club</span>
            </h2>
            <p className="text-white/60 font-light max-w-xl mx-auto mb-8 text-sm md:text-base leading-relaxed">
              Subscribe to receive private charter announcements, seasonal safari itineraries, and priority access to our newest luxury fleet additions.
            </p>

            <form onSubmit={(e) => { e.preventDefault(); alert('Thank you for joining the Skiva Elite Club!'); }} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                required
                placeholder="Enter your email address..."
                className="flex-1 px-5 py-4 bg-[#0a0a0a] border border-white/20 rounded-sm text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-gold-base focus:ring-1 focus:ring-gold-base transition-all"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-gold-base text-black font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-gold-light transition-all flex items-center justify-center gap-2 shadow-lg shadow-gold-base/20 whitespace-nowrap"
              >
                Subscribe <Send className="w-3.5 h-3.5" />
              </button>
            </form>
            <p className="text-[11px] text-white/40 mt-4 tracking-wider uppercase">Zero spam. Pure luxury curation. Unsubscribe anytime.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
